import React from "react";
import styled from "styled-components";
import {
  ResponsiveScatterPlot,
  ScatterPlotNodeProps,
  ScatterPlotTooltipProps,
} from "@nivo/scatterplot";
import { Empty } from "antd";
import {
  CollaborationScatterSeries,
  CollaborationTensionRow,
} from "../../types/linkageDashboardTypes";
import {
  Card,
  RowContainer,
  ElementContainer,
} from "../../components/containers/styledComponents";
import { CompassParagraph } from "../../components/common/styledComponents";
import {
  greyText,
  lightGreys,
  darkGreen,
  compassLightGreen,
  compassYellow,
  brightRed,
} from "../../styles";
import { formatReadable } from "../../utils/formattingUtils";

// =============================================================================
// HELPERS
// =============================================================================

/** Map tension_rate to a diverging color: green → yellow → red */
const tensionColorScale = (tensionRate: number): string => {
  if (tensionRate < 0.12) return compassLightGreen;
  if (tensionRate < 0.25) return compassYellow;
  return brightRed;
};

/** Scale total_linkages to a node radius between 8 and 40px */
const scaleBubbleSize = (size: number, allSizes: number[]): number => {
  const min = Math.min(...allSizes);
  const max = Math.max(...allSizes);
  if (max === min) return 20;
  const normalized = (size - min) / (max - min);
  return 8 + normalized * 32;
};

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const ChartContainer = styled.div`
  height: 340px;
  width: 100%;
`;

const TooltipBox = styled.div`
  background: white;
  padding: 10px 14px;
  border: 1px solid ${lightGreys.borderLightGrey};
  border-radius: 7px;
  font-family: "Apercu";
  font-size: 12px;
  min-width: 160px;
`;

const LegendBar = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
  gap: 4px;
`;

const GradientBar = styled.div`
  width: 120px;
  height: 8px;
  border-radius: 4px;
  background: linear-gradient(
    to right,
    ${compassLightGreen},
    ${compassYellow},
    ${brightRed}
  );
`;

// =============================================================================
// CUSTOM COMPONENTS
// =============================================================================

interface BubbleNodeData {
  x: number;
  y: number;
  theme: string;
  size: number;
}

const CustomBubbleNode: React.FC<ScatterPlotNodeProps<BubbleNodeData>> = ({
  node,
}) => {
  const data = node.data as BubbleNodeData;
  const color = tensionColorScale(data.y);
  // We access raw `size` from data and use a proportional radius
  const radius = node.size / 2;

  return (
    <circle
      cx={node.x}
      cy={node.y}
      r={radius}
      fill={color}
      fillOpacity={0.75}
      stroke={color}
      strokeWidth={1.5}
      strokeOpacity={0.9}
      style={{ cursor: "pointer" }}
    />
  );
};

const CustomTooltip = ({
  node,
}: ScatterPlotTooltipProps<BubbleNodeData>): JSX.Element => {
  const data = node.data as BubbleNodeData;
  return (
    <TooltipBox>
      <CompassParagraph bold={true} fontSize={13} mb={4}>
        {formatReadable(data.theme)}
      </CompassParagraph>
      <ElementContainer w="100%" jc="space-between" ai="center" mt={2}>
        <CompassParagraph color={greyText} fontSize={11}>
          Cross-manager rate:
        </CompassParagraph>
        <CompassParagraph fontSize={11}>
          {(data.x * 100).toFixed(1)}%
        </CompassParagraph>
      </ElementContainer>
      <ElementContainer w="100%" jc="space-between" ai="center" mt={2}>
        <CompassParagraph color={greyText} fontSize={11}>
          Tension rate:
        </CompassParagraph>
        <CompassParagraph fontSize={11}>
          {(data.y * 100).toFixed(1)}%
        </CompassParagraph>
      </ElementContainer>
      <ElementContainer w="100%" jc="space-between" ai="center" mt={2}>
        <CompassParagraph color={greyText} fontSize={11}>
          Total linkages:
        </CompassParagraph>
        <CompassParagraph fontSize={11}>{data.size}</CompassParagraph>
      </ElementContainer>
    </TooltipBox>
  );
};

// =============================================================================
// COMPONENT
// =============================================================================

interface CollaborationTensionChartProps {
  data: CollaborationScatterSeries[];
  rawData: CollaborationTensionRow[];
  isLoading: boolean;
}

export const CollaborationTensionChart: React.FC<
  CollaborationTensionChartProps
> = ({ data, rawData, isLoading }) => {
  const isEmpty = !data || data.length === 0 || data[0]?.data.length === 0;
  const allSizes = rawData.map((r) => r.total_linkages);

  // Dynamic Y-axis max based on actual data
  const maxTension =
    rawData.length > 0 ? Math.max(...rawData.map((r) => r.tension_rate)) : 1;
  const yMax = Math.min(Math.ceil((maxTension + 0.05) * 10) / 10, 1);

  // Create size-mapped data for Nivo
  const sizedData: CollaborationScatterSeries[] = data.map((series) => ({
    ...series,
    data: series.data.map((point) => ({
      ...point,
      // Nivo uses nodeSize; we embed the visual radius in a custom field
      size: point.size,
    })),
  }));

  return (
    <Card h="500px" isLoading={isLoading}>
      <RowContainer mt={0} mb={8}>
        <CompassParagraph fontSize={16} bold={true}>
          Collaboration vs. Tension
        </CompassParagraph>
      </RowContainer>
      <CompassParagraph fontSize={12} color={greyText} mb={12}>
        Each bubble is one engagement theme. Position shows collaboration rate
        vs. tension rate; size shows volume.
      </CompassParagraph>

      {isEmpty && !isLoading ? (
        <ElementContainer
          ai="center"
          jc="center"
          h="200px"
          w="100%"
          flexDirection="column"
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No collaboration/tension data available for the selected filters."
          />
        </ElementContainer>
      ) : (
        <>
          <ChartContainer>
            <ResponsiveScatterPlot
              data={sizedData}
              margin={{ top: 20, right: 20, bottom: 60, left: 70 }}
              xScale={{ type: "linear", min: 0, max: 1 }}
              yScale={{ type: "linear", min: 0, max: yMax }}
              nodeSize={(node): number => {
                const d = node.data as unknown as BubbleNodeData;
                return scaleBubbleSize(d.size, allSizes) * 2;
              }}
              nodeComponent={CustomBubbleNode}
              useMesh={true}
              tooltip={CustomTooltip}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                tickValues: 5,
                format: (v): string => `${Math.round(Number(v) * 100)}%`,
                legend: "Cross-manager collaboration rate",
                legendPosition: "middle",
                legendOffset: 46,
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                tickValues: 5,
                format: (v): string => `${Math.round(Number(v) * 100)}%`,
                legend: "Tension rate",
                legendPosition: "middle",
                legendOffset: -55,
              }}
              gridXValues={[0.5]}
              gridYValues={[0.12, 0.25]}
              theme={{
                text: { fontFamily: "Apercu", fontSize: 11, fill: darkGreen },
                axis: {
                  ticks: {
                    text: {
                      fontFamily: "Apercu",
                      fontSize: 11,
                      fill: greyText,
                    },
                  },
                  legend: {
                    text: {
                      fontFamily: "Apercu",
                      fontSize: 12,
                      fill: darkGreen,
                    },
                  },
                },
                grid: {
                  line: {
                    stroke: "#e2e8f0",
                    strokeDasharray: "4 4",
                  },
                },
              }}
              animate={true}
              motionConfig="gentle"
            />
          </ChartContainer>

          <LegendBar>
            <CompassParagraph fontSize={11} color={greyText} mr={6}>
              Tension:
            </CompassParagraph>
            <CompassParagraph fontSize={10} color={compassLightGreen}>
              Low
            </CompassParagraph>
            <GradientBar />
            <CompassParagraph fontSize={10} color={brightRed}>
              High
            </CompassParagraph>
          </LegendBar>
        </>
      )}
    </Card>
  );
};
